/**
 * Patient-alert review pop-up (DEV-3110 / DEV-3162).
 *
 * Mirrors `accounts.models.practice.PatientAlertPopupMode` on the backend. The practice picks one
 * mode; each staff member may opt out only in OPTIONAL. Undefined (backend predates the field)
 * behaves exactly like OFF, so nothing new renders against an older backend.
 */
export const PATIENT_ALERT_POPUP_MODE = {
  OFF: "off",
  OPTIONAL: "optional",
  REQUIRED: "required",
  REQUIRED_DAILY: "required_daily"
} as const;

export type PatientAlertPopupMode =
  (typeof PATIENT_ALERT_POPUP_MODE)[keyof typeof PATIENT_ALERT_POPUP_MODE];

export const PATIENT_ALERT_POPUP_MODE_OPTIONS: {
  value: PatientAlertPopupMode;
  label: string;
  description: string;
}[] = [
  {
    value: PATIENT_ALERT_POPUP_MODE.OFF,
    label: "Off",
    description:
      "Alerts stay in the patient header and Alerts tab only. This is how Suno works today."
  },
  {
    value: PATIENT_ALERT_POPUP_MODE.OPTIONAL,
    label: "On, staff can turn it off for themselves",
    description:
      "Everyone gets the pop-up by default. Each person can switch it off in their own Patient Chart settings."
  },
  {
    value: PATIENT_ALERT_POPUP_MODE.REQUIRED,
    label: "Required for everyone",
    description:
      "Nobody can switch it off. Each person reviews an alert once, and again only if it is added to or changed. Every review is recorded with the person's name and time."
  },
  {
    value: PATIENT_ALERT_POPUP_MODE.REQUIRED_DAILY,
    label: "Required for everyone, confirmed every day",
    description:
      "Nobody can switch it off. Each person reviews a patient's alerts the first time they open that patient each day, even if nothing has changed. Strictest option, best for practices where alerts drive billing or clinical decisions."
  }
];

export const getPatientAlertPopupModeLabel = (
  mode: PatientAlertPopupMode | undefined
) =>
  PATIENT_ALERT_POPUP_MODE_OPTIONS.find((option) => option.value === mode)
    ?.label ?? "Off";

export const isPatientAlertPopupOn = (
  mode: PatientAlertPopupMode | undefined
) => mode !== undefined && mode !== PATIENT_ALERT_POPUP_MODE.OFF;

export const isPatientAlertPopupRequired = (
  mode: PatientAlertPopupMode | undefined
) =>
  mode === PATIENT_ALERT_POPUP_MODE.REQUIRED ||
  mode === PATIENT_ALERT_POPUP_MODE.REQUIRED_DAILY;

export const isPatientAlertPopupDaily = (
  mode: PatientAlertPopupMode | undefined
) => mode === PATIENT_ALERT_POPUP_MODE.REQUIRED_DAILY;

/**
 * Whether the pop-up should show for one staff member, given the practice mode and the person's
 * own `patient_chart.show_alert_popup` preference. The preference only counts in OPTIONAL; an unset
 * preference (undefined/null) means "on".
 */
export const shouldShowPatientAlertPopup = (
  mode: PatientAlertPopupMode | undefined,
  showAlertPopupPreference: boolean | null | undefined
) => {
  if (!isPatientAlertPopupOn(mode)) return false;
  if (isPatientAlertPopupRequired(mode)) return true;
  return showAlertPopupPreference !== false;
};

/** How many alerts the chart loads for the pill, hover preview and pop-up — one shared request. */
/**
 * The alert red the chart already uses (`patient-alert-chip`, the header pill and its preview
 * define the same pair locally). Shared here so the settings pages can colour their explainer
 * without importing the chart's dialog module.
 */
export const PATIENT_ALERT_RED = "#E23B3B";
export const PATIENT_ALERT_RED_DARK = "#C42E2E";

export const CHART_PATIENT_ALERTS_LIMIT = 25;

export const PATIENT_ALERT_LIST_QUERY =
  "{id,alert_description,created_at,created_by,discarded_at,alert_date}";

/** The list projection plus the per-requester review state (only valid when the pop-up is on). */
export const PATIENT_ALERT_LIST_QUERY_WITH_REVIEW =
  "{id,alert_description,created_at,created_by,discarded_at,alert_date,needs_review,acknowledged_by_me_at}";

export const PATIENT_ALERT_REVIEW_SETTINGS_PATH =
  "/settings/personal?tab=patient-chart";
